import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import clsx from "clsx";

import mainClasses from "../styles.module.scss";
import { FormInputBox, FormInputIcon } from "../formControl";

import type { IInfiniteSearchProps } from "./types";
import localClasses from "./styles.module.scss";

import { ButtonLoader } from "@components/atoms/loader";
import { Button } from "@components/atoms/button";
import { Typography } from "@/components/atoms/typography";
import { useDebounced, useReadOnly } from "@/hooks";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";
import { Icon } from "@/components/atoms/icons";

export const InfiniteSearch = memo(function InfiniteSearch<T>(
  props: IInfiniteSearchProps<T>,
) {
  const { disabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);

  return (
    <InputPopover
      ariaRole={"listbox"}
      disabled={disabled || readOnly}
      listNavLoop={false}
    >
      <InfiniteSearchInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

function InfiniteSearchInput<T>(props: IInfiniteSearchProps<T>) {
  const {
    value = { key: "", value: "" },
    onChange,
    readOnly,
    disabled,
    isError,
    onBlur,
    filter,
    onMouseDown,
    onFocus,
    data: items,
    searchInputChangeHandler: searchChange,
    onGetNext,
    isFetchingNextPage,
    onKeyDown,
    isLoading,
    validation,
    renderItem,
    placeholder,
    ...rest
  } = props;

  const {
    ref: loadMoreContainerRef,
    inView,
    entry: loadMoreEntry,
  } = useInView();

  const { t } = useTranslation("common");

  const {
    activeIndex,
    setActiveIndex,
    refs,
    getReferenceProps,
    setOpen: setIsOpen,
    open: isOpen,
    context,
  } = useInputPopoverContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState("");

  // we need another state to filter value.
  // because on input focus or blur or item selection,
  // value has changed and it cause refetching.
  // we want to apply the filter only if the user is typing.
  const [filterValue, setFilterValue] = useState(filter);
  const debouncedValue = useDebounced(filterValue, 500);

  useEffect(() => {
    searchChange?.(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useEffect(() => {
    if (context.open === false) {
      // clear filter
      setFilterValue("");
    }
  }, [context.open]);

  const [_isInputIsFocused, setIsInputIsFocused] = useState(false);
  const isInputIsFocused = _isInputIsFocused && !readOnly;

  function itemClickHandler() {
    // if (activeIndex !== null && activeIndex !== items.length) {
    //   const item = items[activeIndex];
    //   const value = getValue(type, item);

    //   setSearchValue(value);
    //   onChange?.({
    //     ...item,
    //     key:
    //       type === "terminologies"
    //         ? (item as ITerminologiesSearchEndpoint)?.code
    //         : item.id?.toString(),
    //     value,
    //   });
    //   setIsOpen(false);
    // }

    if (activeIndex === null) return;
    const activeItem = items[activeIndex]!;

    onChange?.(activeItem);
    setSearchValue(activeItem.value);
    setIsOpen(false);
  }

  useEffect(() => {
    if (inView) {
      onGetNext?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const focusOnInput = () => inputRef.current?.focus();

  // is there any selected value or input is empty
  const hasSelectedValue = value.key !== "";

  // can user clear or modify the value or not
  const canClearItem =
    hasSelectedValue &&
    !readOnly &&
    !disabled &&
    !isLoading &&
    !isError &&
    !isInputIsFocused;

  const getInputPlaceholder = () => {
    if (hasSelectedValue) return isInputIsFocused ? value.value : undefined;
    return placeholder;
  };

  const inputPlaceholder = getInputPlaceholder();

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={!!disabled}
        readOnly={!!readOnly}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
        }}
      >
        <div className={mainClasses.inputContainer}>
          <div className={clsx(["position-relative", "w-100", "d-flex"])}>
            {!isInputIsFocused && hasSelectedValue && (
              <div
                style={{ maxWidth: `${inputRef.current?.clientWidth}px` }}
                className={clsx([mainClasses.chip, localClasses.chip])}
              >
                <Typography
                  htmlAttribute={{ title: value.value }}
                  createSpan
                  ellipsis
                >
                  {value.value}
                </Typography>
              </div>
            )}

            <input
              {...getReferenceProps({
                "aria-autocomplete": "list",
                onKeyDown(event) {
                  switch (event.key) {
                    case "Enter":
                      // prevent form submitting
                      event.preventDefault();
                      itemClickHandler();
                      break;

                    case "Tab":
                      setIsOpen(false);
                      break;
                    case "ArrowDown":
                      if (
                        loadMoreEntry?.target &&
                        activeIndex === items.length - 1
                      )
                        onGetNext?.();
                      break;
                    default:
                      if (event.key.length === 1 && !isOpen) setIsOpen(true);
                      break;
                  }
                },
              })}
              className={clsx([mainClasses.input, "w-100"])}
              readOnly={readOnly}
              disabled={disabled}
              ref={inputRef}
              value={searchValue}
              autoComplete={"off"}
              placeholder={inputPlaceholder}
              onChange={(event) => {
                const value = event.target.value;

                setSearchValue(value);
                setFilterValue(value);
              }}
              onFocus={(event) => {
                setIsOpen(true);
                setIsInputIsFocused(true);
                onFocus?.(event);
              }}
              onBlur={(event) => {
                setSearchValue("");
                setIsInputIsFocused(false);
                // if you close the menu when input loose focus thn u can not select the load more button.
                // setIsOpen(false)
                onBlur?.(event);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();

                setIsOpen(true);
                onMouseDown?.(event);
              }}
              {...rest}
            />
          </div>
        </div>
        <FormInputIcon
          iconName={canClearItem ? "CloseCircle" : "Magnifer"}
          type={canClearItem ? "actionButton" : "bgIcon"}
          onMouseDown={(event) => {
            if (canClearItem) {
              // prevent to focus out input
              event.preventDefault();
              // prevent to call `FormControlBox` onMouseDownEvent
              event.stopPropagation();
            }
          }}
          onClick={() => {
            // clear the value
            if (canClearItem) {
              onChange?.({ key: "", value: "", flag: "" });
              // setFilterValue("");
            }
          }}
          {...{ isLoading, isError, disabled, readOnly }}
        />
      </FormInputBox>
      <InputPopoverContent>
        {filter.trim() && (
          <div className={"d-flex gap-m1 py-xs px-m1"}>
            <Icon
              color={"secondary-text"}
              fontSize={"base"}
              name={"Magnifer"}
            />
            <Typography createSpan variant={"caption3"}>
              <u>{`${filter}`}</u>
            </Typography>
          </div>
        )}
        {items.length === 0 && !isLoading && (
          <Typography variant={"caption1"} center>
            {t("nodata")}
          </Typography>
        )}
        {items.map((item, index) => (
          <InputMenuItemPopover
            key={item.key}
            index={index}
            onClick={itemClickHandler}
            selected={item.key === value.key}
            onMouseDown={
              // cause we don't want to lose input's focus on icon clicking event
              (event) => {
                event.preventDefault();
                setActiveIndex(index);
              }
            }
          >
            {renderItem ? (
              renderItem(item)
            ) : (
              <Typography>{item.value}</Typography>
            )}
          </InputMenuItemPopover>
        ))}
        {(isFetchingNextPage || isLoading) && (
          <div
            className={"w-100 d-flex justify-content-center align-items-center"}
            style={{ height: "2rem" }}
          >
            <ButtonLoader />
          </div>
        )}
        {!!onGetNext && (
          <div
            className={"w-100 d-flex justify-content-center"}
            ref={loadMoreContainerRef}
          >
            <Button
              mode={"default"}
              label={t("load_more")}
              variant={"transparent"}
              color={"primary"}
              onClick={() => onGetNext?.()}
            />
          </div>
        )}
      </InputPopoverContent>
    </>
  );
}
